// The Details column of a params listing: one line saying what a field's
// settings amount to — "required · integer · 1 to 10 · default 5" — beside its
// name and its type.
//
// WHY IT IS A WIDGET, AND WHY THE SCHEMA NAMES IT
//
// A param's `config` is a `oneOf` resolved by the sibling `type`, and
// form-schema picks the widget for a resolved branch from the BRANCH document
// — the `widget` a listing or fields config names for the property is not
// consulted once the branch is substituted (getField's second pass in
// widgets/object). So the one way to put a summary in that column is for every
// `/Types/Param/Type/*` document to name this widget, which each param
// registry does in one place (params/types/index.js) rather than eleven.
//
// That makes this the widget for the config in EVERY mode, so `Edit` is the
// object widget's own: the modal a row opens still edits the branch as the
// form it always was, and only the read-only rendering — a listing cell, a
// read-only form — becomes the summary.
//
// WHAT IT KNOWS
//
// The registries differ — pagews has paragraph and image, wfws has time, the
// form-schema demo has object-selector — and this reads whichever keys the
// config carries, so a type it has no branch for shows the flags and nothing
// else rather than throwing. The flags (`required`, `hidden`) live on the
// param, not the config, which is why the View walks up to the row.
import React from "react";
import * as ObjectWidget from "@repoflow/form-schema/widgets/object";

const CUT = 48;

const trim = (text, cut = CUT) => {
  const value = String(text);
  return value.length > cut ? value.slice(0, cut - 1) + "…" : value;
};

const isSet = (value) => value !== undefined && value !== null && value !== "";

/**
 * The fragments describing one `type` + `config` pair, in the order they read
 * best. Exported for the tests and for anything else that wants the words
 * without the cell.
 */
export const describe = (type, config) => {
  const cfg = config && typeof config === "object" ? config : {};
  const out = [];

  switch (type) {
    case "text": {
      if (cfg.type && cfg.type !== "text") out.push(cfg.type);
      const min = cfg.minLength;
      const max = cfg.maxLength;
      if (min > 0 && isSet(max)) out.push(`${min}–${max} characters`);
      else if (min > 0) out.push(`at least ${min} characters`);
      else if (isSet(max) && max !== 128) out.push(`up to ${max} characters`);
      if (cfg.pattern) out.push(`matches ${trim(cfg.pattern)}`);
      if (isSet(cfg.default)) out.push(`default “${trim(cfg.default)}”`);
      break;
    }
    case "checkbox":
      out.push(cfg.default ? "checked by default" : "unchecked by default");
      break;
    case "options": {
      const list = Array.isArray(cfg.list) ? cfg.list : [];
      out.push(list.length ? trim(list.join(" | "), 64) : "no options yet");
      if (isSet(cfg.default)) out.push(`default ${cfg.default}`);
      if (cfg.allowEmpty) out.push("may be empty");
      break;
    }
    case "number": {
      out.push(cfg.type === "number" ? "decimal" : "integer");
      const lo = cfg.minimum;
      const hi = cfg.maximum;
      if (isSet(lo) && isSet(hi)) out.push(`${lo} to ${hi}`);
      else if (isSet(lo)) out.push(`at least ${lo}`);
      else if (isSet(hi)) out.push(`at most ${hi}`);
      if (isSet(cfg.default)) out.push(`default ${cfg.default}`);
      break;
    }
    case "color":
      if (isSet(cfg.default)) {
        out.push(
          <>
            <span
              aria-hidden="true"
              style={{
                display: "inline-block",
                width: "0.8em",
                height: "0.8em",
                verticalAlign: "middle",
                border: "1px solid rgba(0,0,0,.2)",
                background: cfg.default,
              }}
            />{" "}
            {cfg.default}
          </>
        );
      }
      break;
    case "date":
      // `format` is wfws' — pagews' date param stores an instant and has no
      // format to speak of, so it says nothing here beyond a default.
      if (cfg.format === "date-time") out.push("date and time");
      else if (cfg.format === "date") out.push("calendar date");
      if (isSet(cfg.default)) out.push(`default ${trim(cfg.default)}`);
      break;
    case "time":
      out.push(`every ${isSet(cfg.interval) ? cfg.interval : 15} minutes`);
      break;
    case "icon":
      if (isSet(cfg.default)) {
        out.push(
          <>
            <i className={`fa fa-${cfg.default}`} aria-hidden="true" />{" "}
            {cfg.default}
          </>
        );
      }
      break;
    case "object":
      out.push(cfg.type ? `subform ${cfg.type}` : "no subform chosen");
      if (cfg.nullable) out.push("empty by default");
      break;
    case "list": {
      // A list's config is the item's own declaration, so the item is described
      // by the same words — "of address", "of options: a | b".
      if (!cfg.type) {
        out.push("no item type chosen");
      } else if (cfg.type === "object") {
        const item = cfg.config || {};
        out.push(item.type ? `of ${item.type}` : "of an unchosen subform");
      } else {
        out.push(`of ${cfg.type}`);
        out.push(...describe(cfg.type, cfg.config));
      }
      break;
    }
    case "image":
      if (cfg.default && cfg.default.url) out.push(`default ${trim(cfg.default.url)}`);
      break;
    case "object-selector": {
      out.push(cfg.selector ? `by ${cfg.selector}` : "no selector chosen");
      const list = Array.isArray(cfg.list) ? cfg.list : [];
      if (list.length) {
        out.push(trim(list.map(({ value, type }) => `${value} → ${type}`).join(", "), 64));
      }
      if (cfg.nullable) out.push("empty by default");
      break;
    }
    default:
      break;
  }

  return out;
};

// The param this config belongs to. `field.parent` is the object the field is
// a property of, and for a listing cell that is the row; walking is cheaper
// than being wrong about the depth, and identity on `config` is what says
// which row when a list of lists nests the same shape.
const paramOf = (field, value) => {
  let node = field ? field.parent : null;

  while (node) {
    if (node.values && node.values.config === value) return node.values;
    node = node.parent;
  }

  return field && field.parent && field.parent.values ? field.parent.values : {};
};

export const View = ({ value, field }) => {
  const param = paramOf(field, value);
  const flags = [];

  if (param.required) flags.push("required");
  if (param.hidden) flags.push("hidden");

  const parts = [...flags, ...describe(param.type, value)];

  if (parts.length === 0) {
    return <span className="text-muted">—</span>;
  }

  return (
    <span className="text-muted small">
      {parts.map((part, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 ? " · " : null}
          {part}
        </React.Fragment>
      ))}
    </span>
  );
};

export const Edit = (props) => <ObjectWidget.Edit {...props} />;
